import { getApi, postApi } from './apiService';

/**
 * All lease-agreement API routes live here so the backend contract can be
 * swapped in one place. The components never reference a URL directly.
 *
 * `send` uploads the lease and delivers it to the tenant in a single multipart
 * call. `list` backs the landlord Leases page. `status` and `sign` are not
 * implemented on the backend yet — while they are null the landlord card skips
 * its status lookup and the tenant signing card stays hidden. Set them to the
 * real paths to switch that UI on.
 */
export const LEASE_ENDPOINTS = {
  send: '/lease/send',
  status: null,
  sign: null,
  list: '/lease/my-leases',
};

// Lease status: 0:draft (uploaded, not sent), 1:sent to tenant, 2:signed, 3:declined
export const LEASE_STATUS = {
  DRAFT: '0',
  SENT: '1',
  SIGNED: '2',
  DECLINED: '3',
};

const LEASE_STATUS_LABELS = {
  [LEASE_STATUS.DRAFT]: 'Not Sent',
  [LEASE_STATUS.SENT]: 'Awaiting Signature',
  [LEASE_STATUS.SIGNED]: 'Signed',
  [LEASE_STATUS.DECLINED]: 'Declined',
};

const LEASE_STATUS_VARIANT = {
  [LEASE_STATUS.DRAFT]: 'draft',
  [LEASE_STATUS.SENT]: 'sent',
  [LEASE_STATUS.SIGNED]: 'signed',
  [LEASE_STATUS.DECLINED]: 'declined',
};

export const getLeaseStatusInfo = (status) => {
  const key = status == null ? LEASE_STATUS.DRAFT : String(status);
  return {
    key,
    label: LEASE_STATUS_LABELS[key] ?? 'Not Sent',
    variant: LEASE_STATUS_VARIANT[key] ?? 'draft',
  };
};

export const ACCEPTED_LEASE_MIME_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];

export const ACCEPTED_LEASE_EXTENSIONS = '.pdf,.doc,.docx';

export const MAX_LEASE_FILE_SIZE = 10 * 1024 * 1024; // 10 MB

/** Returns an error message when the picked file is not an acceptable lease, else null. */
export const validateLeaseFile = (file) => {
  if (!file) return 'Please choose a lease document to send.';
  const name = (file.name || '').toLowerCase();
  const hasValidExtension = /\.(pdf|doc|docx)$/.test(name);
  const hasValidType = file.type ? ACCEPTED_LEASE_MIME_TYPES.includes(file.type) : false;
  if (!hasValidExtension && !hasValidType) {
    return 'Lease document must be a PDF, DOC or DOCX file.';
  }
  if (file.size > MAX_LEASE_FILE_SIZE) {
    return 'Lease document must be 10 MB or smaller.';
  }
  return null;
};

const firstValue = (data, ...keys) => {
  if (!data || typeof data !== 'object') return null;
  for (const key of keys) {
    const value = data[key];
    if (value != null && value !== '') return value;
  }
  return null;
};

export const formatLeaseTimestamp = (value) => {
  if (value == null || value === '') return '';
  const str = String(value);
  const asNum = Number(str);
  const date = !Number.isNaN(asNum) && /^\d+$/.test(str) ? new Date(asNum) : new Date(str);
  if (Number.isNaN(date.getTime())) return str;
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

/** Map the API row onto one shape regardless of the field names the backend uses. */
export const normalizeLease = (raw) => {
  const row = Array.isArray(raw) ? raw[0] : raw;
  if (!row || typeof row !== 'object') return null;
  const id = firstValue(row, 'id', 'lease_id', 'leaseId');
  const documentUrl = firstValue(row, 'document', 'lease_url', 'document_url', 'file_url', 'url', 'leaseUrl');
  const title = firstValue(row, 'title', 'lease_title', 'document_title');
  const sendUrl = firstValue(row, 'send_url', 'sendUrl', 'embed_url', 'prepare_url');
  if (id == null && documentUrl == null && title == null && sendUrl == null) return null;
  // The e-sign provider hands back a prepare page: the lease only reaches the
  // tenant after the landlord places the signature fields there and sends it.
  const requiresFieldPlacement =
    row.requires_field_placement === true ||
    row.requiresFieldPlacement === true ||
    String(firstValue(row, 'requires_field_placement', 'requiresFieldPlacement') ?? '') === 'true';
  return {
    id: id != null ? String(id) : null,
    documentId: firstValue(row, 'document_id', 'documentId'),
    title: title || 'Lease Agreement',
    documentUrl: documentUrl ? String(documentUrl) : null,
    sendUrl: sendUrl ? String(sendUrl) : null,
    requiresFieldPlacement,
    fileName: firstValue(row, 'file_name', 'fileName', 'original_name'),
    status: String(
      firstValue(row, 'status', 'lease_status') ??
        (requiresFieldPlacement ? LEASE_STATUS.DRAFT : LEASE_STATUS.SENT)
    ),
    sentAt: firstValue(row, 'sent_at', 'sentAt', 'created_at', 'createdAt'),
    signedAt: firstValue(row, 'signed_at', 'signedAt'),
    signedBy: firstValue(row, 'signed_by', 'signature_name', 'signedBy'),
    signedDocumentUrl: firstValue(row, 'signed_document_url', 'signed_lease_url', 'signedDocumentUrl'),
    declineReason: firstValue(row, 'decline_reason', 'declineReason', 'tenant_comment'),
  };
};

/**
 * Badge text for a lease row. A lease still awaiting field placement has been
 * uploaded but not delivered, so it must not read as "Awaiting Signature".
 */
export const getLeaseDisplayStatus = (lease) => {
  if (lease?.requiresFieldPlacement) {
    return { key: LEASE_STATUS.DRAFT, label: 'Needs Field Placement', variant: 'draft' };
  }
  return getLeaseStatusInfo(lease?.status);
};

/** Origins the e-sign prepare page is served from (any regional BoldSign host) — checked before trusting a postMessage. */
const LEASE_EMBED_ORIGIN_PATTERN = /^https:\/\/([a-z0-9-]+\.)*boldsign\.(com|dev)$/i;

export const isLeaseEmbedOrigin = (origin) => LEASE_EMBED_ORIGIN_PATTERN.test(String(origin || ''));

const LEASE_EMBED_SENT_EVENTS = ['ondocumentsent', 'ondocumentsentsuccess'];
const LEASE_EMBED_FAILED_EVENTS = ['ondocumentsendingfailed', 'ondocumentdraftfailed'];

/**
 * Read the outcome out of a prepare-page postMessage. Returns 'sent', 'failed',
 * or null for the many events we deliberately ignore (saves, resizes, etc).
 */
export const getLeaseEmbedOutcome = (data) => {
  const raw =
    typeof data === 'string'
      ? data
      : data && typeof data === 'object'
        ? data.eventType ?? data.event ?? data.type ?? data.status ?? data.action ?? data.name
        : null;
  if (!raw || typeof raw !== 'string') return null;
  const event = raw.trim().toLowerCase();
  if (LEASE_EMBED_SENT_EVENTS.includes(event)) return 'sent';
  if (LEASE_EMBED_FAILED_EVENTS.includes(event)) return 'failed';
  return null;
};

export const isPdfUrl = (url) => (url ? /\.pdf(\?|#|$)/i.test(String(url)) : false);

/**
 * Upload the lease and send it to the tenant for signature — one multipart call.
 * POST /lease/send — property_id, tenant_id, title, document
 */
export const sendLease = async ({ propertyId, tenantId, title, file }) => {
  const formData = new FormData();
  formData.append('property_id', propertyId);
  formData.append('tenant_id', tenantId);
  // Title and document are optional — the backend can send a lease without them.
  if (title) formData.append('title', title);
  if (file) formData.append('document', file);
  const response = await postApi(LEASE_ENDPOINTS.send, formData);
  if (response?.statusCode === 200 || response?.statusCode === 201) {
    const payload = response?.data ?? response;
    return { lease: normalizeLease(payload?.lease ?? payload), message: response?.message };
  }
  const error = new Error(response?.message || 'Failed to send lease agreement.');
  error.response = { data: response };
  throw error;
};

/**
 * GET the lease for a property/tenant. Returns null when the endpoint is not
 * configured yet, or when no lease exists (the backend may answer 404 or an
 * empty payload for that case).
 */
export const fetchLease = async ({ propertyId, tenantId }) => {
  if (!LEASE_ENDPOINTS.status) return null;
  const params = {};
  if (propertyId != null) params.property_id = propertyId;
  if (tenantId != null) params.tenant_id = tenantId;
  const response = await getApi(LEASE_ENDPOINTS.status, { params });
  if (response?.statusCode === 200) {
    const payload = response?.data ?? response;
    return normalizeLease(payload?.lease ?? payload);
  }
  if (response?.statusCode === 404) return null;
  const error = new Error(response?.message || 'Failed to load lease agreement.');
  error.response = { data: response };
  throw error;
};

/** Tenant e-signature: typed legal name plus explicit consent. */
export const signLease = async ({ leaseId, propertyId, signatureName }) => {
  if (!LEASE_ENDPOINTS.sign) {
    throw new Error('Lease signing is not available yet.');
  }
  const response = await postApi(LEASE_ENDPOINTS.sign, {
    lease_id: leaseId,
    property_id: propertyId,
    signature_name: signatureName,
    agreed: true,
  });
  if (response?.statusCode === 200) {
    const payload = response?.data ?? response;
    return { lease: normalizeLease(payload?.lease ?? payload), message: response?.message };
  }
  const error = new Error(response?.message || 'Failed to sign lease agreement.');
  error.response = { data: response };
  throw error;
};

/** `$1,850/mo`, or a dash when the backend has no rent on the row. */
export const formatRent = (value) => {
  if (value == null || value === '') return '—';
  const amount = Number(value);
  if (Number.isNaN(amount)) return String(value);
  return `$${amount.toLocaleString('en-US', { maximumFractionDigits: 2 })}/mo`;
};

const joinName = (...parts) => parts.filter(Boolean).join(' ').trim();

/**
 * A row of the landlord Leases table: the lease itself plus the property and
 * tenant it belongs to. The backend may nest those or flatten them onto the
 * row, so both shapes are read here rather than in the component.
 */
export const normalizeLeaseListRow = (raw) => {
  const lease = normalizeLease(raw);
  if (!lease || !raw || typeof raw !== 'object') return null;

  const property = raw.property || raw.Property || raw.property_details || {};
  const tenant = raw.tenant || raw.Tenant || raw.tenant_details || {};

  const address = firstValue(property, 'address') || firstValue(raw, 'property_address') || '';
  const unit = firstValue(property, 'unit_number', 'unitNumber');
  const city = firstValue(property, 'city');
  const state = firstValue(property, 'state');
  const street = unit ? `${address}, ${unit}` : String(address || '');
  const cityState = [city, state].filter(Boolean).join(', ');

  const tenantName =
    joinName(firstValue(tenant, 'first_name', 'firstName'), firstValue(tenant, 'last_name', 'lastName')) ||
    firstValue(tenant, 'name', 'full_name') ||
    firstValue(raw, 'tenant_name') ||
    '';

  return {
    ...lease,
    propertyId: firstValue(raw, 'property_id', 'propertyId') ?? firstValue(property, 'id'),
    tenantId: firstValue(raw, 'tenant_id', 'tenantId') ?? firstValue(tenant, 'id'),
    propertyAddress: street,
    propertyCityState: cityState,
    propertyCode: firstValue(property, 'property_code', 'propertyCode') ?? firstValue(raw, 'property_code'),
    tenantName,
    tenantEmail: firstValue(tenant, 'email') ?? firstValue(raw, 'tenant_email'),
    monthlyRent:
      firstValue(raw, 'monthly_rent', 'monthlyRent', 'rent_amount', 'rent') ??
      firstValue(property, 'monthly_rent', 'monthlyRent'),
  };
};

/**
 * GET every lease this landlord has sent, with its property, tenant and rent.
 * Backs the Leases page. Returns an empty page while the endpoint is unset.
 * GET /lease/my-leases?page&limit&search
 */
export const fetchLeases = async ({ page = 1, limit = 10, search = '' } = {}) => {
  const empty = { leases: [], currentPage: 1, pageSize: limit, totalPages: 1, totalItems: 0 };
  if (!LEASE_ENDPOINTS.list) return empty;

  const params = new URLSearchParams({ page: String(page), limit: String(limit) });
  if (search && search.trim()) params.append('search', search.trim());

  let response;
  try {
    response = await getApi(`${LEASE_ENDPOINTS.list}?${params.toString()}`);
  } catch (error) {
    // The route is not deployed yet on every environment — an unimplemented
    // route reads as "no leases" rather than an error the landlord must act on.
    const httpStatus = error?.response?.status;
    if (httpStatus === 404 || httpStatus === 501) return empty;
    throw error;
  }

  if (response?.statusCode !== 200) {
    if (response?.statusCode === 404) return empty;
    const error = new Error(response?.message || 'Failed to load leases.');
    error.response = { data: response };
    throw error;
  }

  const payload = response?.data ?? {};
  // Tolerate the paginated envelope (`data.data`) and a plain array alike.
  const rows = Array.isArray(payload)
    ? payload
    : payload.data ?? payload.leases ?? payload.rows ?? [];

  return {
    leases: (Array.isArray(rows) ? rows : []).map(normalizeLeaseListRow).filter(Boolean),
    currentPage: Number(payload.currentPage) || page,
    pageSize: Number(payload.pageSize) || limit,
    totalPages: Number(payload.totalPages) || 1,
    totalItems: Number(payload.totalItems) || (Array.isArray(rows) ? rows.length : 0),
  };
};
